import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { overtimeAPI } from '@/services/overtime';
import { 
  CheckCircle, XCircle, Clock, Calendar, Filter, Search, Download  // ✅ Search موجود هنا
} from 'lucide-react';
import { cn } from '@/lib/utils';

const AdminOvertimePage = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const res = await overtimeAPI.listAll(user.id);
      console.log('API Response:', res);
      if (res.success) {
        setRequests(res.requests);
      }
    } catch (error) {
      console.error('Load error:', error);
      toast({ title: 'Error', description: 'Failed to load requests', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (requestId, status) => {
    try {
      const res = await overtimeAPI.updateStatus({
        id: requestId,
        status,
        actionBy: user.name,
        actionById: user.id,
      });
      if (res.success) {
        setRequests(prev => prev.map(r => 
          r.id === requestId 
            ? { ...r, status, action_by: user.name, action_by_id: user.id, action_date: new Date().toISOString() }
            : r
        ));
        toast({ 
          title: `${status} ✅`, 
          description: `Request ${status.toLowerCase()}`, 
          className: status === 'Approved' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200' 
        });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' });
    }
  };

  const bulkUpdate = async () => {
    if (selectedRequests.length === 0) return;
    const status = filterStatus === 'Approved' ? 'Approved' : 'Rejected';
    for (const id of selectedRequests) {
      await updateStatus(id, status);
    }
    setSelectedRequests([]);
  };

  const filteredRequests = requests.filter(req => {
    const matchesStatus = filterStatus === 'all' || req.status === filterStatus;
    const matchesSearch = !searchTerm || 
      req.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||  // ✅ safe access
      req.reason?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getStatusBadge = (status) => {
    const colors = {
      'Approved': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'Pending': 'bg-amber-100 text-amber-800 border-amber-200',
      'Rejected': 'bg-red-100 text-red-800 border-red-200'
    };
    return cn('px-3 py-1 rounded-full text-xs font-semibold border', colors[status] || 'bg-gray-100 text-gray-800');
  };

  return (
    <TooltipProvider>
      <div className="space-y-8 max-w-7xl mx-auto pb-12">
        <Helmet><title>Admin Overtime - Stone Beam</title></Helmet>

        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-navy-steal">Overtime Requests</h1>
            <p className="text-stone-500 mt-1">Review and approve/reject employee overtime requests</p>
          </div>
          <div className="text-sm text-stone-500">
            Total: {requests.length} | Pending: {requests.filter(r => r.status === 'Pending').length}
          </div>
        </div>

        {/* Filters & Bulk Actions */}
        <Card className="border-stone-200">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-3 items-center">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={selectedRequests.length === filteredRequests.length && filteredRequests.length > 0}
                    onCheckedChange={(checked) => setSelectedRequests(checked ? filteredRequests.map(r => r.id) : [])}
                  />
                  <span className="font-medium text-sm">
                    {selectedRequests.length} of {filteredRequests.length} selected
                  </span>
                </div>

                {selectedRequests.length > 0 && (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => bulkUpdate()}
                      className="bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve {selectedRequests.length}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => bulkUpdate()}
                      className="bg-red-50 border-red-200 hover:bg-red-100"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject {selectedRequests.length}
                    </Button>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 items-center w-full sm:w-auto">
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input 
                    placeholder="Search employee or reason..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Approved">Approved</SelectItem>
                    <SelectItem value="Rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>

                <Button variant="outline" size="sm" onClick={loadRequests} disabled={loading}>
                  <Download className="w-4 h-4 mr-1" />
                  Refresh
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Requests Table */}
        <Card className="shadow-md border-stone-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-safety-orange" />
              All Overtime Requests
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-stone-500">Loading requests...</div>
            ) : filteredRequests.length === 0 ? (
              <div className="p-12 text-center text-stone-400">
                <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">No overtime requests found</p>
                <p className="text-sm mt-1">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50/50">
                    <tr>
                      <th className="p-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider w-12"></th>
                      <th className="p-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Employee</th>
                      <th className="p-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider w-24">Date</th>
                      <th className="p-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider w-20">Hours</th>
                      <th className="p-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Reason</th>
                      <th className="p-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider w-28">Status</th>
                      <th className="p-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider w-40">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {filteredRequests.map((req) => (
                      <tr key={req.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="p-4">
                          <Checkbox
                            checked={selectedRequests.includes(req.id)}
                            onCheckedChange={() => {
                              setSelectedRequests(prev =>
                                prev.includes(req.id)
                                  ? prev.filter(id => id !== req.id)
                                  : [...prev, req.id]
                              );
                            }}
                          />
                        </td>
                        <td className="p-4">
                          <div className="font-medium text-stone-900">{req.user_name}</div>
                          <div className="text-xs text-stone-500">{req.department}</div>
                        </td>
                        <td className="p-4">
                          {new Date(req.date).toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </td>
                        <td className="p-4">
                          <div className="font-bold text-xl text-navy-steal">{req.hours}h</div>
                        </td>
                        <td className="p-4 max-w-[200px]">
                          <Tooltip>
                            <TooltipTrigger className="truncate block">{req.reason}</TooltipTrigger>
                            <TooltipContent>{req.reason}</TooltipContent>
                          </Tooltip>
                        </td>
                        <td className="p-4">
                          <Badge className={cn(getStatusBadge(req.status))}>
                            {req.status}
                          </Badge>
                        </td>
                        <td className="p-4">
                          {req.status === 'Pending' && (
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                onClick={() => updateStatus(req.id, 'Approved')}
                                className="bg-emerald-50 hover:bg-emerald-100 border-emerald-200 text-emerald-800 h-8 px-3"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button 
                                size="sm"
                                variant="destructive"
                                onClick={() => updateStatus(req.id, 'Rejected')}
                                className="h-8 px-3"
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                          {req.status !== 'Pending' && (
                            <div className="text-xs text-stone-500">
                              {req.action_by} - {new Date(req.action_date).toLocaleString()}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
};

export default AdminOvertimePage;
